import { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, X, Building2, User as UserIcon } from 'lucide-react';
import { getGoogleAuthUrl } from '../config/google-oauth';
import CareerGrowthLogo from './CareerGrowthLogo';
import AccountTypeTabs from './AccountTypeTabs';
import ConsentCheckboxes from './ConsentCheckboxes';
import { pushDataLayer } from '../utils/dataLayer';
import { trackEvent } from '../utils/amplitude';
import { api, APIError } from '../api/client';
import { useAuthStore } from '../stores/useAuthStore';
import { useB2BAuthStore } from '../stores/useB2BAuthStore';
import { LEGAL_VERSIONS } from '../config/legalVersions';

const isEmailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const ACCOUNT_TYPE_STORAGE_KEY = 'auth-account-type';

const getApiErrorMessage = (err, fallbackMessage) => {
  if (!(err instanceof APIError)) {
    return fallbackMessage;
  }

  const details = err.data?.detail;
  if (typeof details === 'string' && details.trim()) {
    return details;
  }

  if (Array.isArray(details) && details.length > 0) {
    const first = details[0];
    if (typeof first === 'string' && first.trim()) {
      return first;
    }
    if (first && typeof first === 'object') {
      const message = first.msg || first.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  if (typeof err.message === 'string' && err.message.trim()) {
    return err.message;
  }

  return fallbackMessage;
};

// Initial accountType resolution: URL > localStorage > 'candidate'.
const resolveInitialAccountType = (pathname) => {
  if (typeof pathname === 'string' && pathname.startsWith('/b2b/')) {
    return 'employer';
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(ACCOUNT_TYPE_STORAGE_KEY);
      if (stored === 'candidate' || stored === 'employer') {
        return stored;
      }
    } catch {
      /* ignore storage errors */
    }
  }
  return 'candidate';
};

export default function AuthPanel({
  mode = 'route',
  defaultTab = 'login',
  onClose,
  onGuestContinue,
  redirectTo,
  lockAccountType,
  onAuthSuccess,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [accountType, setAccountType] = useState(() =>
    lockAccountType || resolveInitialAccountType(location?.pathname)
  );

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerWorkspaceName, setRegisterWorkspaceName] = useState('');

  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(false);
  // Consent state — distinct keys for C2C vs B2B per ConsentCheckboxes API.
  const [consent, setConsent] = useState({
    tos_accepted: false,
    marketing_consent: false,
    terms_accepted: false,
    dpa_accepted: false,
    authorized_signatory: false,
  });
  const updateConsent = (key, value) => setConsent((prev) => ({ ...prev, [key]: value }));

  // Drag-to-dismiss logic for mobile bottom sheet
  const [dragging, setDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const startY = useRef(0);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const isEmployer = accountType === 'employer';

  const handleTouchStart = (e) => {
    if (!isMobile || mode !== 'modal') return;
    startY.current = e.touches[0].clientY;
    setDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!dragging || !isMobile || mode !== 'modal') return;
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0) setDragY(diff);
  };

  const handleTouchEnd = () => {
    if (!dragging || !isMobile || mode !== 'modal') return;
    setDragging(false);
    if (dragY > 120) {
      if (onClose) onClose();
    } else {
      setDragY(0);
    }
  };

  // Sync activeTab with route changes (login/register/b2b-auth).
  useEffect(() => {
    if (mode !== 'route') return;
    const path = location.pathname || '';
    if (path === '/register') {
      setActiveTab('register');
    } else if (path === '/login') {
      setActiveTab('login');
    } else if (path.startsWith('/b2b/')) {
      // Default to login on /b2b/auth — register users explicitly switch tabs.
      setActiveTab((prev) => prev === 'register' ? 'register' : 'login');
    }
  }, [location.pathname, mode]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (mode !== 'route') return;
    if (accountType === 'employer') {
      // Stay on /b2b/auth — employer side has only one auth route.
      return;
    }
    navigate(tab === 'register' ? '/register' : '/login');
  };

  const switchAccountType = (next) => {
    setAccountType(next);
    setLoginError('');
    setRegisterError('');
    try {
      window.localStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }

    pushDataLayer({
      event: 'ui_interaction',
      action: 'click',
      element: 'account_type_tab',
      account_type: next,
      data_source: 'front',
    });

    if (mode === 'route') {
      // Bridge URL to match the chosen tab.
      if (next === 'employer' && !location.pathname.startsWith('/b2b/')) {
        navigate('/b2b/auth');
      } else if (next === 'candidate' && location.pathname.startsWith('/b2b/')) {
        navigate(activeTab === 'register' ? '/register' : '/login');
      }
    }
  };

  const handleGoogleLogin = () => {
    pushDataLayer({
      event: 'ui_interaction',
      action: 'click',
      element: 'google_auth_btn',
      account_type: accountType,
      location: 'iframe',
      data_source: 'front',
    });
    window.location.href = getGoogleAuthUrl(accountType);
  };

  const handleForgotPassword = () => {
    pushDataLayer({
      event: 'ui_interaction',
      action: 'click',
      element: 'forgot_password_link',
      location: 'iframe',
      data_source: 'front',
    });
  };

  const login = useAuthStore((state) => state.login);
  const b2bLogin = useB2BAuthStore((state) => state.login);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    pushDataLayer({
      event: 'form_attempt',
      form_name: isEmployer ? 'b2b_login_form' : 'login_form',
      account_type: accountType,
      is_email_valid: isEmailValid(loginEmail) ? 1 : 0,
      is_password_valid: loginPassword && String(loginPassword).length >= 8 ? 1 : 0,
      location: 'iframe',
      data_source: 'front',
    });

    if (!loginEmail || !loginPassword) {
      setLoginError('Please fill in all fields');
      return;
    }

    if (!isEmailValid(loginEmail)) {
      setLoginError('Please enter a valid email');
      return;
    }

    if (loginPassword.length < 8) {
      setLoginError('Password must be at least 8 characters');
      return;
    }

    setLoginLoading(true);

    try {
      if (isEmployer) {
        const data = await api.post('/api/b2b/auth/login', {
          email: loginEmail,
          password: loginPassword,
        });

        pushDataLayer({
          event: 'auth_success',
          type: 'login',
          method: 'email',
          account_type: 'employer',
          user_id: data.member?.id ? String(data.member.id) : null,
          data_source: 'back',
        });
        trackEvent('b2b_login');

        // Same-domain после merge: сразу пишем в B2B-store и редиректим внутрь.
        b2bLogin({ token: data.token, member: data.member, workspace: data.workspace });
        onAuthSuccess?.();
        navigate('/b2b/dashboard');
        return;
      }

      const data = await api.post('/api/auth/login', {
        email: loginEmail,
        password: loginPassword,
      });

      if (data.token && data.user) {
        login(data.user, data.token);
      }

      pushDataLayer({
        event: 'auth_success',
        type: 'login',
        method: 'email',
        account_type: 'candidate',
        user_id: data.user?.id ? String(data.user.id) : null,
        data_source: 'back',
      });
      trackEvent('login');

      const candidate = redirectTo || location.state?.redirectTo;
      const isSafe = typeof candidate === 'string' && candidate.startsWith('/') && !candidate.startsWith('//');
      navigate(isSafe ? candidate : '/search');
    } catch (err) {
      const fallback = isEmployer
        ? 'Could not sign in. Check your email and password — or create a workspace if this is your first time.'
        : 'An error occurred during login';
      setLoginError(getApiErrorMessage(err, fallback));
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');

    const registerPasswordValue = String(registerPassword || '');
    const registerPasswordValid =
      registerPasswordValue.length >= 8 &&
      /[a-zA-Z]/.test(registerPasswordValue) &&
      /[0-9]/.test(registerPasswordValue);

    pushDataLayer({
      event: 'form_attempt',
      form_name: isEmployer ? 'b2b_signup_form' : 'signup_form',
      account_type: accountType,
      is_email_valid: isEmailValid(registerEmail) ? 1 : 0,
      is_password_valid: registerPasswordValid ? 1 : 0,
      location: 'iframe',
      data_source: 'front',
    });

    if (!registerEmail || !registerPassword || !registerConfirmPassword) {
      setRegisterError('Please fill in all fields');
      return;
    }

    if (isEmployer && !registerWorkspaceName.trim()) {
      setRegisterError('Please enter a workspace name (e.g., your company or agency)');
      return;
    }

    if (!isEmailValid(registerEmail)) {
      setRegisterError('Please enter a valid email');
      return;
    }

    if (registerPassword.length < 8) {
      setRegisterError('Password must be at least 8 characters');
      return;
    }

    if (!registerPasswordValid) {
      setRegisterError('Password must contain both letters and numbers');
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }

    if (isEmployer) {
      if (!consent.terms_accepted || !consent.dpa_accepted || !consent.authorized_signatory) {
        setRegisterError('You must accept the Business Terms, DPA, and confirm signatory authority to continue.');
        return;
      }
    } else if (!consent.tos_accepted) {
      setRegisterError('You must accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setRegisterLoading(true);

    try {
      if (isEmployer) {
        const data = await api.post('/api/b2b/auth/register', {
          email: registerEmail,
          password: registerPassword,
          name: registerName.trim() || null,
          workspace_name: registerWorkspaceName.trim(),
          terms_accepted: true,
          dpa_accepted: true,
          authorized_signatory: true,
          terms_version: LEGAL_VERSIONS.BUSINESS_TERMS,
          dpa_version: LEGAL_VERSIONS.DPA,
        });

        pushDataLayer({
          event: 'auth_success',
          type: 'sign_up',
          method: 'email',
          account_type: 'employer',
          user_id: data.member?.id ? String(data.member.id) : null,
          data_source: 'back',
        });
        trackEvent('b2b_first_login');

        b2bLogin({ token: data.token, member: data.member, workspace: data.workspace });
        onAuthSuccess?.();
        navigate('/b2b/dashboard');
        return;
      }

      const data = await api.post('/api/auth/register', {
        email: registerEmail,
        password: registerPassword,
        tos_accepted: true,
        privacy_accepted: true,
        tos_version: LEGAL_VERSIONS.TOS,
        privacy_version: LEGAL_VERSIONS.PRIVACY,
        marketing_consent: Boolean(consent.marketing_consent),
      });

      if (data.token && data.user) {
        login(data.user, data.token);

        pushDataLayer({
          event: 'auth_success',
          type: 'sign_up',
          method: 'email',
          account_type: 'candidate',
          user_id: data.user?.id ? String(data.user.id) : null,
          data_source: 'back',
        });
        trackEvent('first_login');

        if (mode === 'modal' && onClose) {
          onClose();
        }
        navigate('/profile');
      } else {
        setRegisterError('Account created, but auto-login failed. Please log in.');
      }
    } catch (err) {
      const fallback = isEmployer
        ? 'Could not create your workspace. Please try again.'
        : 'An error occurred during registration';
      setRegisterError(getApiErrorMessage(err, fallback));
    } finally {
      setRegisterLoading(false);
    }
  };

  const headings = isEmployer
    ? {
        loginTitle: 'Welcome back',
        loginSub: 'Sign in to your recruiter workspace',
        registerTitle: 'Create your workspace',
        registerSub: 'Start screening candidates in minutes',
        loginCta: 'Log in to workspace',
        registerCta: 'Create workspace',
        switchToRegister: "Don't have a workspace? ",
        switchToLogin: 'Already have a workspace? ',
        switchToRegisterCTA: 'Create workspace',
        switchToLoginCTA: 'Log in',
      }
    : {
        loginTitle: 'Welcome back',
        loginSub: 'Enter your credentials to continue',
        registerTitle: 'Create account',
        registerSub: 'Start your journey with Career Growth',
        loginCta: 'Login',
        registerCta: 'Sign Up',
        switchToRegister: "Don't have an account? ",
        switchToLogin: 'Already have an account? ',
        switchToRegisterCTA: 'Sign Up',
        switchToLoginCTA: 'Login',
      };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: isMobile && mode === 'modal' ? `translateY(${dragY}px)` : 'none',
        transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
      }}
      className={`w-full bg-white shadow-2xl relative overflow-hidden ${
        mode === 'modal'
          ? 'rounded-t-[24px] sm:rounded-[32px] max-h-[88vh] overflow-y-auto'
          : 'rounded-[32px] px-8 py-10'
      }`}
    >
      {/* Mobile drag handle */}
      {isMobile && mode === 'modal' && (
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 bg-gray-200 rounded-full" />
        </div>
      )}

      {mode === 'modal' && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-gray-400 hover:text-gray-600 sm:block hidden"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <div className={`${mode === 'modal' ? 'px-6 sm:px-8 py-6 sm:py-10' : ''}`}>
        <div className="flex flex-col items-center text-center">
          <Link to="/" className="flex flex-col items-center gap-2">
            <CareerGrowthLogo />
          </Link>
        </div>

        {/* Account-type tabs (candidate vs employer) */}
        {!lockAccountType && (
          <div className="mt-6">
            <AccountTypeTabs
              value={accountType}
              onChange={switchAccountType}
              disabled={loginLoading || registerLoading}
            />
          </div>
        )}

        {/* Inner login/register tabs */}
        <div className="mt-4 bg-[#F6F7FB] rounded-2xl p-1 flex">
          <button
            type="button"
            onClick={() => switchTab('login')}
            className={`flex-1 py-2 text-sm font-semibold rounded-2xl transition ${
              activeTab === 'login' ? 'bg-white text-[#1E293B] shadow-sm' : 'text-gray-500'
            }`}
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => switchTab('register')}
            className={`flex-1 py-2 text-sm font-semibold rounded-2xl transition ${
              activeTab === 'register' ? 'bg-white text-[#1E293B] shadow-sm' : 'text-gray-500'
            }`}
          >
            Sign Up
          </button>
        </div>

        <div className="mt-8">
          <h2 className="text-2xl font-bold text-[#1E293B]">
            {activeTab === 'login' ? headings.loginTitle : headings.registerTitle}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {activeTab === 'login' ? headings.loginSub : headings.registerSub}
          </p>

          {/* Google button */}
          <div className="mt-6">
            <button
              onClick={handleGoogleLogin}
              disabled={loginLoading || registerLoading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-gray-200 rounded-2xl bg-white hover:bg-gray-50 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-gray-700 font-semibold text-sm">
                Continue with Google
              </span>
            </button>
          </div>

          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-100"></div>
            <span className="px-4 text-gray-400 text-[10px] font-bold uppercase tracking-wider">or</span>
            <div className="flex-grow border-t border-gray-100"></div>
          </div>

          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {loginError}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Email</label>
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    placeholder={isEmployer ? 'you@agency.com' : 'your@email.com'}
                    className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    disabled={loginLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Password</label>
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={loginLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {!isEmployer && (
                <div className="flex justify-end mt-1">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-[#F47920] text-sm font-semibold hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-[#F47920] hover:bg-[#E06810] text-white text-[16px] font-bold py-4 rounded-2xl transition-all shadow-md shadow-orange-200 active:scale-[0.99] disabled:opacity-50"
                disabled={loginLoading}
              >
                {loginLoading ? 'Logging in...' : headings.loginCta}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              {registerError && (
                <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {registerError}
                </div>
              )}

              {isEmployer && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Workspace name</label>
                    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="e.g., Northstar Talent"
                        className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                        value={registerWorkspaceName}
                        onChange={(e) => setRegisterWorkspaceName(e.target.value)}
                        disabled={registerLoading}
                        autoComplete="organization"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 ml-1">Your team or agency name. You can change it later.</p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Your name <span className="text-gray-400 font-normal">(optional)</span></label>
                    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="First name"
                        className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        disabled={registerLoading}
                        autoComplete="given-name"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Email</label>
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    placeholder={isEmployer ? 'you@agency.com' : 'your@email.com'}
                    className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    disabled={registerLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Password</label>
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <input
                    type={showRegisterPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    disabled={registerLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 ml-1">Min 8 chars with letters & numbers</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide ml-1">Confirm Password</label>
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/30 px-4 py-3.5 focus-within:border-[#F47920] transition-colors">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <input
                    type={showRegisterConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="flex-1 text-[15px] outline-none bg-transparent text-gray-700"
                    value={registerConfirmPassword}
                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                    disabled={registerLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterConfirm((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showRegisterConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <ConsentCheckboxes
                mode={isEmployer ? 'signupB2B' : 'signupC2C'}
                values={consent}
                onChange={updateConsent}
              />

              <button
                type="submit"
                className="w-full bg-[#F47920] hover:bg-[#E06810] text-white text-[16px] font-bold py-4 rounded-2xl transition-all shadow-md shadow-orange-200 active:scale-[0.99] disabled:opacity-50 mt-2"
                disabled={registerLoading}
              >
                {registerLoading ? (isEmployer ? 'Creating workspace...' : 'Creating account...') : headings.registerCta}
              </button>
            </form>
          )}

          <div className="mt-8 text-center space-y-3">
            <p className="text-sm text-gray-500">
              {activeTab === 'login' ? headings.switchToRegister : headings.switchToLogin}
              <button
                type="button"
                onClick={() => switchTab(activeTab === 'login' ? 'register' : 'login')}
                className="text-[#F47920] font-bold hover:underline"
              >
                {activeTab === 'login' ? headings.switchToRegisterCTA : headings.switchToLoginCTA}
              </button>
            </p>

            {mode === 'modal' && (onGuestContinue || true) && (
              <button
                type="button"
                onClick={onGuestContinue || onClose}
                className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-4"
              >
                Continue without an account
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
